import { Flex, Form, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useAppContext } from 'business/contextProviders/useAppContext';
import { usePermissions } from 'business/contextProviders/usePermissions';
import { useSyncBoardConfigData } from 'business/quality-control/hooks/use-sync-board-config-data';
import {
  PreloadForm,
  PreloadFormValues,
} from 'business/quality-control/pages/record-segment-defect/components/preload-form';
import { RangeInfoCard } from 'business/quality-control/pages/record-segment-defect/components/range-info-card';
import { getRangeChecks } from 'business/quality-control/pages/record-segment-defect/components/right-panel/get-range-checks';
import { OfflinePanel } from 'business/quality-control/pages/record-segment-defect/components/right-panel/offline-panel';
import { useBoardContext } from 'business/quality-control/pages/record-segment-defect/context/board-context';
import { useGetRangeStats } from 'business/quality-control/pages/record-segment-defect/hooks/data/get-range-stats';
import { useSyncQualityData } from 'business/quality-control/pages/record-segment-defect/hooks/use-sync-quality-data';
import Routes from 'config/routes';
import { useNetworkStatus } from 'technical/network/use-network-status';
import Button from 'ui/button';

import styles from './index.module.scss';

export const RingRangeSelection = ({
  resetSelectedRing,
}: {
  resetSelectedRing: () => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isOnline } = useNetworkStatus();

  const { currentConstructionSiteId } = useAppContext();
  invariant(currentConstructionSiteId, 'No construction site id');

  const {
    isDataManager,
    modules: {
      QUALITY_CONTROL: { isConstructionSiteManager },
    },
  } = usePermissions();

  const [form] = Form.useForm<PreloadFormValues>();

  const startRing = Form.useWatch('startRing', form);
  const endRing = Form.useWatch('endRing', form);

  const { isRangeDefined, isInRange, isOrderValid, isRangeValid } =
    getRangeChecks(startRing, endRing);

  const { data: rangeStats, isLoading: isRangeStatsLoading } = useGetRangeStats(
    startRing,
    endRing,
    isRangeValid,
  );

  const {
    handleSync: fetchBoardConfigData,
    isSyncing: isSyncingBoardConfigData,
  } = useSyncBoardConfigData(currentConstructionSiteId);

  const { handleSync: fetchRingsData, isSyncing: isSyncingQualityData } =
    useSyncQualityData(currentConstructionSiteId, startRing, endRing);

  const { setIsDrawingEnable, setBoardConstructionSiteId } = useBoardContext();

  const handleFinish = async () => {
    resetSelectedRing();
    setIsDrawingEnable(false);

    const [boardConfigResult, ringsResult] = await Promise.all([
      fetchBoardConfigData(),
      fetchRingsData(),
    ]);

    if (boardConfigResult.success && ringsResult.success) {
      setBoardConstructionSiteId(currentConstructionSiteId);
    } else {
      // Both syncs already reset their local data before failing (see
      // handleSync in each hook) — clear the marker so it doesn't keep
      // pointing at a board that no longer exists locally.
      setBoardConstructionSiteId(undefined);
    }
  };

  return (
    <Flex vertical className={styles.content}>
      <Flex vertical align="center" gap={16} className={styles.selectRingRange}>
        <Typography.Title level={4} className={styles.title}>
          {t('qualityControl.recordSegmentDefect.rightPanel.title')}
        </Typography.Title>
        {isOnline ? (
          <PreloadForm
            form={form}
            onFinish={handleFinish}
            isRangeDefined={isRangeDefined}
            isOrderValid={isOrderValid}
            isInRange={isInRange}
          />
        ) : (
          <OfflinePanel />
        )}

        {isOnline ? (
          <Flex justify="center">
            <Button
              type="primary"
              disabled={!isRangeValid}
              onClick={() => form.submit()}
              loading={isSyncingBoardConfigData || isSyncingQualityData}
            >
              {t('qualityControl.recordSegmentDefect.rightPanel.loadButton')}
            </Button>
          </Flex>
        ) : null}

        {isOnline && !isRangeStatsLoading && isRangeValid ? (
          <RangeInfoCard
            totalRings={rangeStats?.totalRings}
            validatedRings={rangeStats?.validatedRings}
            defectReports={rangeStats?.defectReports}
            segmentsUnderRepair={rangeStats?.segmentsUnderRepair}
          />
        ) : null}
      </Flex>

      {isOnline && (isDataManager || isConstructionSiteManager) ? (
        <Flex justify="flex-end" style={{ marginTop: 16 }}>
          <Button onClick={() => navigate(Routes.QualityBoardPreview)}>
            {t(
              'qualityControl.recordSegmentDefect.rightPanel.redirectToPreview',
            )}
          </Button>
        </Flex>
      ) : null}
    </Flex>
  );
};
